import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { IDailyStats } from '@feathr/blackbox';
import { Skeleton } from '@feathr/components';
import { cssVar, moment, useId } from '@feathr/hooks';

import type { TDateRange, TTimeframePreset } from '../ReportsConversionsDashboardPage.utils';
import { formatRangeLabel, mergeTrendData } from './KpiTiles.utils';
import TileError from './TileError';

import * as styles from './KpiTiles.css';

// 122px matches the Figma spec for the trend chart height
const CHART_HEIGHT = 122;

function formatTick(value: string): string {
  return moment.utc(value).format('MMM D');
}

export interface IConversionsTrendTileProps {
  currentData: IDailyStats[];
  hasError: boolean;
  isLoading: boolean;
  preset: TTimeframePreset;
  priorData: IDailyStats[];
  range: TDateRange;
}

function ConversionsTrendTile({
  currentData,
  hasError,
  isLoading,
  preset,
  priorData,
  range,
}: Readonly<IConversionsTrendTileProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const currentGradientId = useId();
  const priorGradientId = useId();

  const trendData = useMemo(() => mergeTrendData(currentData, priorData), [currentData, priorData]);

  // cssVar reads getComputedStyle so must stay inside the component
  const tickStyle = {
    fill: cssVar('--color-text-body'),
    fontSize: cssVar('--text-xs'),
    fontWeight: cssVar('--font-medium'),
  };

  const currentLabel = formatRangeLabel(preset, range, false, t);
  const priorLabel = formatRangeLabel(preset, range, true, t);

  if (hasError) {
    return <TileError label={t('Conversions Trend')} />;
  }

  return (
    <Stack gap={theme.spacing['2']}>
      <span className={styles.label}>{t('Conversions Trend')}</span>
      {isLoading ? (
        <Skeleton height={CHART_HEIGHT} />
      ) : (
        <ResponsiveContainer height={CHART_HEIGHT} width={'100%'}>
          <AreaChart data={trendData} margin={{ bottom: 0, left: 0, right: 0, top: 10 }}>
            <defs>
              <linearGradient id={currentGradientId} x1={'0'} x2={'0'} y1={'0'} y2={'1'}>
                <stop offset={'5%'} stopColor={cssVar('--color-neutral-900')} stopOpacity={0.3} />
                <stop offset={1} stopColor={cssVar('--color-neutral-900')} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={priorGradientId} x1={'0'} x2={'0'} y1={'0'} y2={'1'}>
                <stop offset={'5%'} stopColor={cssVar('--color-cyan-400')} stopOpacity={0.3} />
                <stop offset={1} stopColor={cssVar('--color-cyan-400')} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray={'4 4'} vertical={false} />
            <XAxis
              axisLine={false}
              dataKey={'date'}
              interval={'preserveStartEnd'}
              tick={tickStyle}
              tickFormatter={formatTick}
              tickLine={false}
            />
            <YAxis hide={true} />
            <Tooltip />
            <Area
              activeDot={{ r: 3, stroke: cssVar('--color-neutral-900'), strokeWidth: 3 }}
              dataKey={'current'}
              fill={`url(#${currentGradientId})`}
              name={currentLabel}
              stroke={cssVar('--color-neutral-900')}
              strokeWidth={2}
              type={'monotone'}
            />
            <Area
              activeDot={{ r: 3, stroke: cssVar('--color-cyan-400'), strokeWidth: 3 }}
              dataKey={'prior'}
              fill={`url(#${priorGradientId})`}
              name={priorLabel}
              stroke={cssVar('--color-cyan-400')}
              strokeDasharray={'4 4'}
              strokeWidth={2}
              type={'monotone'}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={`${styles.legendDot} ${styles.legendDotCurrent}`} />
          <span className={styles.legendLabel}>{currentLabel}</span>
        </div>
        <div className={styles.legendItem}>
          <div className={`${styles.legendDot} ${styles.legendDotPrior}`} />
          <span className={styles.legendLabel}>{priorLabel}</span>
        </div>
      </div>
    </Stack>
  );
}

export default ConversionsTrendTile;
